import { Checkbox } from '@mui/material';
import Typography from '@mui/material/Typography';
import cx from 'classnames';
import { probeGetConnectionTree } from 'js/actions';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { isEmpty } from 'lodash';
import { ReactComponent as DatabaseIcon } from 'new/elements/flows/Databases/assets/database.svg';
import {
  DatabaseAccordion,
  DatabaseDetails,
  DatabaseTitle,
} from 'new/elements/flows/Databases/components/DatabaseAccordion/DatabaseAccordion';
import { Table } from 'new/elements/flows/Databases/components/Table';
import useDatabases from 'new/elements/flows/Databases/useDatabases';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Virtuoso } from 'react-virtuoso';
import classes from './Database.module.scss';

type DatabaseType = {
  name: string;
  id: string;
  children?: {
    id: number;
    name: string;
    children: any[];
  }[];
};

interface DatabaseProps {
  needsSelection?: boolean;
  selected?: boolean;
  expanded?: boolean;
  loading?: boolean;
  onSelect?: (databaseName: string) => void;
  database: DatabaseType;
  isCDCMode?: boolean;
}

export const Database = memo<DatabaseProps>((props: DatabaseProps) => {
  const { database, needsSelection, isCDCMode } = props;
  const [expanded, setExpanded] = useState(false);
  const loadingProbe = useSelector((state: any) => state.probe.loading?.[database?.id]);
  const dispatch = useAppDispatch();

  const { selection, databaseSelected, selectDatabase, selectTables, credentialId, isDestination } =
    useDatabases();

  const onChangeSelect = (e) => {
    e.stopPropagation();
    const { checked } = e.target;
    selectDatabase(database.name, checked);
    if (database.children) {
      selectTables(
        database.children.map((table) => table.name),
        database.name,
        checked,
      );
    }
  };

  const handleExpand = (e, isExpanded) => {
    setExpanded(isExpanded);
    if (isExpanded && (!database.children || !database.children.length)) {
      dispatch(probeGetConnectionTree(credentialId, false, database));
    }
  };

  useEffect(() => {
    if (selection.__selectionCount__ === 0) {
      selectDatabase(database.name, false);
    }
    if (selection.__selectionCount__ === database.children?.length) {
      selectDatabase(database.name, true);
    }
  }, [selection.__selectionCount__]);

  const Selection = (() => {
    if (!needsSelection) return null;
    if (isDestination) return null;
    return (
      <Checkbox
        onChange={onChangeSelect}
        checked={databaseSelected || false}
        indeterminate={
          databaseSelected &&
          selection.__selectionCount__ !== database.children?.length &&
          selection.__selectionCount__ > 0
        }
        onClick={(e) => e.stopPropagation()}
      />
    );
  })();

  return (
    <li>
      <DatabaseAccordion
        onChange={handleExpand}
        data-testid={`database-node-${database.id}`}
        expanded={expanded && !!database.children}
        TransitionProps={{
          timeout: database.children?.length > 20 ? 0 : 300,
        }}
      >
        <DatabaseTitle loading={loadingProbe}>
          <div className={classes.databaseNameContainer}>
            {Selection}
            <DatabaseIcon />
            <Typography variant="body1">{database.name}</Typography>
          </div>
        </DatabaseTitle>
        <DatabaseDetails>
          <DatabaseContent {...props} loading={loadingProbe} />
        </DatabaseDetails>
      </DatabaseAccordion>
    </li>
  );
});

function DatabaseContent({ loading, database, needsSelection, isCDCMode }: DatabaseProps) {
  const {
    selection,
    resyncSelection,
    tableFilters,
    columnSelections,
    syncModes,
    expandedTables,
    onShowSamples,
    onShowSchema,
    expandTable,
    selectTables,
    selectResyncTables,
    addTableFilters,
    updateColumnSelection,
    updateSyncMode,
    tableMap,
    credentialId,
    isDestination,
  } = useDatabases();
  const dispatch = useAppDispatch();
  const databaseRef = useRef(null);

  const handleTableExpand = useCallback(
    (table) => (e, isExpanded) => {
      if (isDestination) return;
      expandTable(table.name, database.name, isExpanded);
      if (isExpanded && (!table.children || !table.children.length)) {
        onRequestProbeTree(table);
      }
    },
    [database],
  );

  const onRequestProbeTree = (node) => {
    dispatch(probeGetConnectionTree(credentialId, false, node));
  };

  const onChangeSelectTable = useCallback(
    (table, isSwitch = false) =>
      (e) => {
        selectTables([table.name], database.name, isSwitch ? !e.target.checked : e.target.checked);
        e.stopPropagation();
      },
    [database, selectTables],
  );

  const onChangeResyncTable = useCallback(
    (table) => (e) => {
      selectResyncTables([table.name], database.name, e.target.checked);
      e.stopPropagation();
    },
    [database, selectResyncTables],
  );

  const onChangeTableFilter = useCallback(
    (table, filter: string) => {
      addTableFilters(table.name, filter);
    },
    [addTableFilters],
  );

  const onColumnSelectionChange = useCallback(
    (
      tableName: string,
      columnName: string,
      columnType: 'timestamp' | 'autoIncrement',
      isSelected: boolean,
    ) => {
      updateColumnSelection(tableName, columnName, columnType, isSelected);
    },
    [updateColumnSelection],
  );

  const onSyncModeChange = useCallback(
    (tableName: string, syncMode: string) => {
      updateSyncMode(tableName, syncMode);
    },
    [updateSyncMode],
  );

  const onShowTableSchema = useCallback(
    (table) => () => {
      if (!table.children || !table.children.length) {
        onRequestProbeTree(table);
      }
      onShowSchema(table);
    },
    [],
  );

  const onClickShowSample = useCallback(
    (table) => (e) => {
      e.stopPropagation();
      onShowSamples(database, table);
    },
    [],
  );

  if (!loading && (!database.children || !database.children.length)) {
    return <Typography variant="body2">This database has no entries</Typography>;
  }

  if (database.children.length > 500) {
    const itemContent = (index, table) => {
      return (
        <Table
          needsSelection={needsSelection}
          key={table.id}
          modified={!isEmpty(tableMap[table.name])}
          isDestination={isDestination}
          handleExpand={handleTableExpand}
          isExpanded={!!expandedTables[table.name]}
          checked={!!selection[table.name] || false}
          canPreview
          synced={
            // We're reverting the value here when exists because we get from the selection [exclude.tables]
            selection[table.name] === undefined ? true : !selection[table.name]
          }
          database={database}
          table={table}
          isCDCMode={isCDCMode}
          resyncChecked={!!resyncSelection[table.name] || false}
          tableFilter={tableFilters[table.name] || ''}
          columnSelections={columnSelections[table.name]}
          syncMode={syncModes[table.name] || 'incremental'}
          onChangeSelectTable={onChangeSelectTable}
          onChangeResyncTable={onChangeResyncTable}
          onChangeTableFilter={onChangeTableFilter}
          onColumnSelectionChange={onColumnSelectionChange}
          onSyncModeChange={onSyncModeChange}
          onClickShowSample={onClickShowSample}
          onShowTableSchema={onShowTableSchema}
        />
      );
    };

    return (
      <div className={cx(classes.tableWrapper, classes.tableWrapper_virtualized)} ref={databaseRef}>
        <Virtuoso data={database.children} itemContent={itemContent} />
      </div>
    );
  }

  return (
    <div className={cx(classes.tableWrapper)}>
      {database.children.map((table) => (
        <Table
          needsSelection={needsSelection}
          key={table.id}
          modified={!isEmpty(tableMap[table.name])}
          isDestination={isDestination}
          handleExpand={handleTableExpand}
          isExpanded={!!expandedTables[table.name]}
          checked={!!selection[table.name] || false}
          canPreview
          synced={
            // We're reverting the value here when exists because we get from the selection [exclude.tables]
            selection[table.name] === undefined ? true : !selection[table.name]
          }
          database={database}
          table={table}
          isCDCMode={isCDCMode}
          resyncChecked={!!resyncSelection[table.name] || false}
          tableFilter={tableFilters[table.name] || ''}
          columnSelections={columnSelections[table.name]}
          syncMode={syncModes[table.name] || 'incremental'}
          onChangeSelectTable={onChangeSelectTable}
          onChangeResyncTable={onChangeResyncTable}
          onChangeTableFilter={onChangeTableFilter}
          onColumnSelectionChange={onColumnSelectionChange}
          onSyncModeChange={onSyncModeChange}
          onClickShowSample={onClickShowSample}
          onShowTableSchema={onShowTableSchema}
        />
      ))}
    </div>
  );
}
